// The day timeline (B1–B3): one row per person, hours left to right, every
// block at its real time. Behind the blocks, the shift bar (or Day off); above
// the rows, the hour labels and the free-therapists strip; across them, the
// "now" line, with the hours already gone faded.
//
// Positions are PERCENTAGES of the visible hours (calendar-board `placement`),
// so 2 PM–midnight fits the width with no sideways scroll; zoom widens the
// hour column and lets it scroll, with the names pinned on the left.

import { cn } from "@bednbody/ui/lib/utils";
import type { CalendarDispatchLegKind } from "@bednbody/types";
import type { ReactNode } from "react";
import { StatusPill } from "../live-map/LiveMapCards";
import {
  type BlockPhase,
  type BoardBlock,
  type BoardRow,
  clockLabel,
  type MinuteSpan,
  placement,
  type ScopedBoard,
  type SessionBlock,
  serviceLabel,
  shortClock,
  spanLabel,
} from "./calendar-board";
import type { HourCell } from "./calendar-board.hours";
import {
  LEG_LABEL,
  LEG_SHORT,
  PHASE_LABEL,
  personStatusLabel,
  personStatusTone,
} from "./calendar-board.labels";
import { BADGE, BadgeImage, hubUrl, StaffAvatar } from "./CalendarIcons";

/** The pinned name column — also where the hour lane starts. */
const NAME_COL = "13rem";

/** Same state colours as the map: in session green-dark, coming up teal, stuck red. */
export const PHASE_CLASS: Record<BlockPhase, string> = {
  done: "border border-success/25 bg-success/10 text-success",
  now: "bg-primary-dark text-primary-foreground",
  next: "bg-primary text-primary-foreground",
  unassigned: "border-2 border-dashed border-destructive bg-destructive/10 text-destructive",
};

export const LEG_CLASS: Record<CalendarDispatchLegKind, string> = {
  drop_off: "border border-warning/40 bg-warning/15 text-foreground",
  pickup: "border border-info/40 bg-info/15 text-foreground",
  return: "border border-border bg-muted text-muted-foreground",
};

const SHIFT_CLASS = "border border-dashed border-border bg-muted/50";

const LEGEND: readonly { label: string; swatch: string }[] = [
  { label: PHASE_LABEL.done, swatch: PHASE_CLASS.done },
  { label: PHASE_LABEL.now, swatch: PHASE_CLASS.now },
  { label: PHASE_LABEL.next, swatch: PHASE_CLASS.next },
  { label: PHASE_LABEL.unassigned, swatch: PHASE_CLASS.unassigned },
  { label: "Shift hours", swatch: SHIFT_CLASS },
  { label: `Rider ${LEG_LABEL.drop_off.toLowerCase()}`, swatch: LEG_CLASS.drop_off },
  { label: `Rider ${LEG_LABEL.pickup.toLowerCase()}`, swatch: LEG_CLASS.pickup },
  { label: `Rider ${LEG_LABEL.return.toLowerCase()}`, swatch: LEG_CLASS.return },
];

const SELECTED_RING = "ring-2 ring-warning ring-offset-2 ring-offset-card";

interface CalendarTimelineProps {
  board: ScopedBoard;
  strip: readonly HourCell[];
  nowMin: number;
  /** Today's board — draws the now line and fades the past. */
  showNow: boolean;
  /** 1 fits the day; above it the hours widen and scroll. */
  zoom: number;
  selectedBlockId: string | null;
  selectedStaffId: string | null;
  selectedHour: number | null;
  onSelectBlock: (block: BoardBlock) => void;
  onSelectPerson: (staffId: string) => void;
  onSelectHour: (startMin: number) => void;
  /** Shown at the end of the colour key — the demo's "Simulated data" badge. */
  keyNote?: ReactNode;
}

function hoursOf(range: MinuteSpan): number[] {
  const hours: number[] = [];
  for (let start = range.startMin; start < range.endMin; start += 60) hours.push(start);
  return hours;
}

function at(range: MinuteSpan, span: MinuteSpan) {
  const { left, width } = placement(range, span);
  return { left: `${left}%`, width: `${width}%` };
}

function HourLines({ range }: { range: MinuteSpan }) {
  return hoursOf(range).map((start) => (
    <span
      aria-hidden="true"
      className="absolute inset-y-0 w-px bg-border/60"
      key={start}
      style={{ left: at(range, { startMin: start, endMin: start }).left }}
    />
  ));
}

function sessionLines(session: SessionBlock): [string, string] {
  const times = `${shortClock(session.startMin)}–${shortClock(session.endMin)}`;
  if (session.phase === "unassigned") return [serviceLabel(session), `Needs therapist · ${times}`];
  if (session.phase === "now") {
    return [serviceLabel(session), `Now · until ${shortClock(session.endMin)}`];
  }
  return [`${session.phase === "done" ? "✓ " : ""}${serviceLabel(session)}`, times];
}

function Block({
  block,
  range,
  selected,
  onSelect,
}: {
  block: BoardBlock;
  range: MinuteSpan;
  selected: boolean;
  onSelect: () => void;
}) {
  const position = at(range, block);
  if (block.kind !== "session") {
    const label = `${LEG_LABEL[block.kind]} for ${block.ref}, ${spanLabel(block)}`;
    return (
      <button
        aria-label={label}
        className={cn(
          "absolute inset-y-2.5 flex items-center justify-center overflow-hidden rounded-md px-1 text-xs font-bold transition hover:-translate-y-px",
          LEG_CLASS[block.kind],
          selected ? SELECTED_RING : null,
        )}
        onClick={onSelect}
        style={position}
        title={label}
        type="button"
      >
        <span className="truncate">{LEG_SHORT[block.kind]}</span>
      </button>
    );
  }
  const [first, second] = sessionLines(block);
  const label = `${block.phase === "unassigned" ? "Needs a therapist: " : ""}${serviceLabel(block)}, ${block.ref}, ${spanLabel(block)}`;
  return (
    <button
      aria-label={label}
      className={cn(
        "absolute inset-y-1.5 flex flex-col items-start justify-center overflow-hidden rounded-lg px-2 text-left transition hover:-translate-y-px",
        PHASE_CLASS[block.phase],
        // Work in progress stays solid above the past wash; only finished work fades.
        block.phase === "now" ? "z-1" : null,
        selected ? SELECTED_RING : null,
      )}
      onClick={onSelect}
      style={position}
      title={label}
      type="button"
    >
      <span className="w-full truncate text-xs font-bold leading-tight">{first}</span>
      <span className="w-full truncate text-xs leading-tight opacity-90">{second}</span>
    </button>
  );
}

function NameCell({
  row,
  selected,
  onSelect,
}: {
  row: BoardRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      aria-label={`Open ${row.name}'s shift`}
      className={cn(
        "sticky left-0 z-10 flex shrink-0 items-center gap-3 border-r border-border/70 px-4 text-left transition-colors hover:bg-muted/60",
        selected ? "bg-warning/10" : "bg-card",
      )}
      onClick={onSelect}
      style={{ width: NAME_COL }}
      type="button"
    >
      <StaffAvatar role={row.role} status={row.status} />
      <span className="flex min-w-0 flex-col gap-1.5">
        {/* No "· rider" here: the row already sits under its hub's Riders label. */}
        <span className="truncate text-sm font-semibold text-foreground">{row.name}</span>
        <StatusPill label={personStatusLabel(row.status)} tone={personStatusTone(row.status)} />
      </span>
    </button>
  );
}

function PersonRow({
  row,
  range,
  selectedBlockId,
  selectedStaffId,
  onSelectBlock,
  onSelectPerson,
}: {
  row: BoardRow;
  range: MinuteSpan;
  selectedBlockId: string | null;
  selectedStaffId: string | null;
  onSelectBlock: (block: BoardBlock) => void;
  onSelectPerson: (staffId: string) => void;
}) {
  const rider = row.role === "rider";
  return (
    <div className={cn("flex border-b border-border/30 last:border-b-0", rider ? "h-16" : "h-18")}>
      <NameCell
        onSelect={() => onSelectPerson(row.staffId)}
        row={row}
        selected={selectedStaffId === row.staffId}
      />
      {/* Riders support the therapists, so their lane is a shade quieter. */}
      <div className={cn("relative flex-1", rider ? "bg-muted/20" : null)}>
        <HourLines range={range} />
        {row.dayOff ? (
          <button
            className="absolute inset-2 flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted px-3 text-xs font-bold text-muted-foreground"
            onClick={() => onSelectPerson(row.staffId)}
            type="button"
          >
            <BadgeImage src={BADGE.dayOff} />
            Day off
          </button>
        ) : null}
        {row.shift ? (
          <button
            aria-label={`${row.name} shift ${spanLabel(row.shift)}`}
            className={cn(
              "absolute inset-y-1.5 rounded-xl",
              SHIFT_CLASS,
              selectedStaffId === row.staffId ? "border-warning" : null,
            )}
            onClick={() => onSelectPerson(row.staffId)}
            style={at(range, row.shift)}
            type="button"
          />
        ) : null}
        {row.blocks.map((block) => (
          <Block
            block={block}
            key={block.id}
            onSelect={() => onSelectBlock(block)}
            range={range}
            selected={selectedBlockId === block.id}
          />
        ))}
      </div>
    </div>
  );
}

export function CalendarTimeline({
  board,
  strip,
  nowMin,
  showNow,
  zoom,
  selectedBlockId,
  selectedStaffId,
  selectedHour,
  onSelectBlock,
  onSelectPerson,
  onSelectHour,
  keyNote,
}: CalendarTimelineProps) {
  const { range } = board;
  const nowVisible = showNow && nowMin > range.startMin && nowMin < range.endMin;
  const nowLeft = at(range, { startMin: nowMin, endMin: nowMin }).left;
  const empty = board.sections.length === 0 && board.unassigned.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ul
        aria-label="Colour key"
        className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border/70 px-4 py-2.5 text-xs text-muted-foreground"
      >
        {LEGEND.map((item) => (
          <li className="flex items-center gap-1.5" key={item.label}>
            <span aria-hidden="true" className={cn("h-2.5 w-4 rounded-sm", item.swatch)} />
            {item.label}
          </li>
        ))}
        {keyNote ? <li className="ml-auto">{keyNote}</li> : null}
      </ul>

      <div className="relative min-h-0 flex-1 overflow-auto">
        <div
          className="relative min-w-full pb-20"
          style={{ width: `calc(${NAME_COL} + (100% - ${NAME_COL}) * ${zoom})` }}
        >
          {/* Hours and the strip stay on screen while the rows scroll. */}
          <div className="sticky top-0 z-20 border-b border-border/70 bg-card">
            <div className="flex h-11 border-b border-border/50">
              <span
                className="sticky left-0 z-10 flex shrink-0 items-center bg-card px-4 text-xs font-bold text-muted-foreground"
                style={{ width: NAME_COL }}
              >
                Team
              </span>
              <div className="relative flex-1">
                {hoursOf(range).map((start) => (
                  <span
                    className={cn(
                      "absolute bottom-1.5 pl-2 text-xs font-bold",
                      showNow && start + 60 <= nowMin
                        ? "text-muted-foreground/60"
                        : "text-foreground",
                    )}
                    key={start}
                    style={{ left: at(range, { startMin: start, endMin: start }).left }}
                  >
                    {clockLabel(start)}
                  </span>
                ))}
                {nowVisible ? (
                  <span
                    className="absolute top-1 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-destructive px-2 text-xs font-bold leading-5 text-primary-foreground"
                    style={{ left: nowLeft }}
                  >
                    Now {clockLabel(nowMin)}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex bg-muted/30">
              <div
                className="sticky left-0 z-10 flex shrink-0 flex-col justify-center bg-card px-4 py-1"
                style={{ width: NAME_COL }}
              >
                <span className="text-sm font-bold text-foreground">Free therapists</span>
                <span className="text-xs text-muted-foreground">click an hour for details</span>
              </div>
              <div className="relative h-14 flex-1">
                {strip.map((cell) => {
                  const none = cell.free === 0;
                  // Short, because an hour can be ~50 px wide beside an open
                  // sidebar; the full sentence is the button's name and title.
                  const text =
                    cell.onDuty === 0
                      ? "Nobody on"
                      : none
                        ? "None free"
                        : `${cell.free} of ${cell.onDuty} free`;
                  const short = cell.onDuty === 0 ? "Nobody" : none ? "None" : `${cell.free} free`;
                  return (
                    <button
                      aria-label={`${clockLabel(cell.startMin)}: ${text}${cell.waiting ? `, ${cell.waiting} waiting` : ""}`}
                      title={`${clockLabel(cell.startMin)}: ${text}${cell.waiting ? `, ${cell.waiting} waiting` : ""}`}
                      className={cn(
                        "absolute inset-y-1.5 flex flex-col items-start justify-center overflow-hidden rounded-md px-2 text-left transition",
                        none ? "bg-warning/15 text-warning" : "bg-success/10 text-success",
                        cell.past ? "opacity-50" : null,
                        selectedHour === cell.startMin ? SELECTED_RING : null,
                      )}
                      key={cell.startMin}
                      onClick={() => onSelectHour(cell.startMin)}
                      style={{
                        left: `calc(${at(range, { startMin: cell.startMin, endMin: cell.startMin }).left} + 2px)`,
                        width: `calc(${at(range, { startMin: cell.startMin, endMin: cell.startMin + 60 }).width} - 4px)`,
                      }}
                      type="button"
                    >
                      <span className="w-full truncate text-xs font-bold">{short}</span>
                      {cell.waiting > 0 ? (
                        <span className="w-full truncate text-xs font-bold text-destructive">
                          {cell.waiting} waiting
                        </span>
                      ) : cell.onDuty > 0 ? (
                        <span className="w-full truncate text-xs opacity-80">of {cell.onDuty}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="relative min-h-full bg-muted/40">
            {empty ? (
              <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                Nobody is scheduled for this view. Clear the search or filters to see everyone.
              </p>
            ) : null}

            {board.unassigned.length > 0 ? (
              <section aria-label="Needs a therapist" className="border-b border-border bg-card">
                <h3 className="relative z-1 flex items-center gap-2 border-b border-border/60 bg-destructive/5 px-4 py-1.5 text-xs">
                  <span className="sticky left-4 inline-flex items-center gap-1.5 font-bold text-destructive">
                    <BadgeImage src={BADGE.needsTherapist} />
                    Needs a therapist
                  </span>
                  <span className="text-muted-foreground">
                    {board.unassigned.length}{" "}
                    {board.unassigned.length === 1 ? "booking has" : "bookings have"} nobody yet
                  </span>
                </h3>
                <div className="flex h-14 border-b border-border/50">
                  <div
                    className="sticky left-0 z-10 flex shrink-0 items-center gap-2.5 border-r border-border/70 bg-card px-3"
                    style={{ width: NAME_COL }}
                  >
                    <BadgeImage size="lg" src={BADGE.needsTherapist} />
                    <span className="flex min-w-0 flex-col gap-1">
                      <span className="truncate text-sm font-semibold text-destructive">
                        Not assigned
                      </span>
                      <StatusPill label="Click to assign" tone="danger" />
                    </span>
                  </div>
                  <div className="relative flex-1">
                    <HourLines range={range} />
                    {board.unassigned.map((block) => (
                      <Block
                        block={block}
                        key={block.id}
                        onSelect={() => onSelectBlock(block)}
                        range={range}
                        selected={selectedBlockId === block.id}
                      />
                    ))}
                  </div>
                </div>
              </section>
            ) : null}

            {board.sections.map((section) => {
              const groups = [
                {
                  key: "therapists",
                  label: "Therapists",
                  rows: section.rows.filter((row) => row.role === "therapist"),
                },
                {
                  key: "riders",
                  label: "Riders",
                  rows: section.rows.filter((row) => row.role === "rider"),
                },
              ].filter((group) => group.rows.length > 0);
              return (
                // Each hub is its own block: a gap of page colour above it and a
                // header you can find at a glance (Leon, 2026-10-23: "crowded").
                <section
                  aria-label={`${section.hubName} hub`}
                  className="mt-4 border-y border-border bg-card first:mt-0"
                  key={section.hubId}
                >
                  <h3 className="relative z-1 flex h-11 items-center gap-2 border-b border-border bg-muted/60 px-4">
                    <span className="sticky left-4 inline-flex items-center gap-2 text-sm font-bold text-foreground">
                      <BadgeImage size="md" src={hubUrl} />
                      {section.hubName} hub
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {[
                        section.therapistCount
                          ? `${section.therapistCount} therapist${section.therapistCount === 1 ? "" : "s"}`
                          : null,
                        section.riderCount
                          ? `${section.riderCount} rider${section.riderCount === 1 ? "" : "s"}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </h3>
                  {groups.map((group) => (
                    <div key={group.key}>
                      <h4 className="relative z-1 flex h-7 items-end border-b border-border/40 bg-card px-4 pb-1">
                        <span className="sticky left-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {group.label} · {group.rows.length}
                        </span>
                      </h4>
                      {group.rows.map((row) => (
                        <PersonRow
                          key={row.staffId}
                          onSelectBlock={onSelectBlock}
                          onSelectPerson={onSelectPerson}
                          range={range}
                          row={row}
                          selectedBlockId={selectedBlockId}
                          selectedStaffId={selectedStaffId}
                        />
                      ))}
                    </div>
                  ))}
                </section>
              );
            })}

            {nowVisible ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0"
                style={{ left: NAME_COL }}
              >
                <div className="absolute inset-y-0 left-0 bg-card/45" style={{ width: nowLeft }} />
                <div
                  className="absolute inset-y-0 z-2 w-0.5 -translate-x-1/2 bg-destructive"
                  style={{ left: nowLeft }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
